<template>
    <section class="py-16 lg:py-[150px] px-5 lg:px-[120px] text-white" :style="{ background: backgroundColor }">
        <div class="mx-auto">
            <div class="grid md:grid-cols-2 gap-[50px] items-center">
                <div class="relative" :class="imagePosition === 'right' ? 'order-2' : 'order-1'">
                    <img :alt="imageAlt" class="rounded-lg object-contain" :src="$asset(image, 'public')" />
                </div>
                <div :class="imagePosition === 'right' ? 'order-1' : 'order-2'">
                    <h2 class="text-[28px] md:text-4xl lg:text-[44px] lg:leading-[57px] font-semibold mb-6" :class="titleColor">
                        <span v-html="renderTitle()"></span>
                    </h2>
                    <p class="text-[18px] mb-8 font-pro leading-tight lg:leading-relaxed" :class="descriptionColor">{{ description }}</p>
                    <div class="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                        <router-link
                            v-if="showCta"
                            :to="ctaLink"
                            class="text-center w-full lg:w-fit justify-center inline-flex items-center gap-2 text-white px-4 py-4 rounded-full font-bold text-md transition hover:opacity-90"
                            :style="{ background: `linear-gradient(to top, ${buttonGradientFrom}, ${buttonGradientTo})` }"
                        >
                            {{ ctaText }}
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </router-link>
                        <div
                            v-else
                            class="text-white px-2 lg:px-6 py-3 rounded-full font-semibold transition text-xs lg:text-md"
                            :style="{ backgroundColor: fallbackCtaBackgroundColor }"
                        >
                            {{ fallbackCtaText }}
                        </div>
                        <div v-if="priceText" class="text-xl font-bold leading-tight lg:leading-relaxed" :class="priceColor">{{ priceText }}</div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    backgroundColor: {
        type: String,
        default: 'linear-gradient(to bottom, #072551, #020E20)'
    },
    title: {
        type: String,
        default: ''
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    titleHighlightGradientTo: {
        type: String,
        default: '#0C7475'
    },
    titleColor: {
        type: String,
        default: 'text-white'
    },
    description: {
        type: String,
        default: ''
    },
    descriptionColor: {
        type: String,
        default: 'text-gray-200'
    },
    image: {
        type: String,
        default: ''
    },
    imageAlt: {
        type: String,
        default: ''
    },
    imagePosition: {
        type: String,
        default: 'left',
        validator: (value) => ['left', 'right'].includes(value)
    },
    ctaText: {
        type: String,
        default: ''
    },
    ctaLink: {
        type: String,
        default: '/'
    },
    showCta: {
        type: Boolean,
        default: true
    },
    buttonGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    buttonGradientTo: {
        type: String,
        default: '#0C7475'
    },
    priceText: {
        type: String,
        default: ''
    },
    priceColor: {
        type: String,
        default: 'text-blue'
    },
    fallbackCtaText: {
        type: String,
        default: ''
    },
    fallbackCtaBackgroundColor: {
        type: String,
        default: '#1E40AF'
    }
});

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const highlightRegex = new RegExp(`(${props.titleHighlight})`, 'g');

    return props.title.replace(
        highlightRegex,
        `<span class="bg-gradient-to-t text-transparent bg-clip-text" style="background: linear-gradient(to top, ${props.titleHighlightGradientFrom}, ${props.titleHighlightGradientTo}); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">$1</span>`
    );
};
</script>
